import * as React from 'react';
import * as ReactDOM from 'react-dom';
import {
  Credentials,
  ErrorTypes,
  OnStateChangedCallback,
  OnUserRegisteredCallback,
  RegistrationFlowInterface,
  RegistrationFlowState,
  StateTypes,
  RegistrationFlowPayload,
} from '@central-products/interface-registration-flow';
import { ACCOUNT_EVENT_IDS } from '@central-products/interface-account-actions';
import { LoginState, StateType as SessionState } from '@central-products/interface-session-manager';
import {
  Jurisdiction,
  LoginHandler,
  OnActionRequested,
  OnSubmitSuccessArguments,
  RegistrationFlowConfig,
  RegistrationFlowDependencies,
  RegistrationContainerProps,
  ChatRequestArguments,
} from './types';

const DEPOSIT_REQUIRED_ACTION_TIMEOUT = 2000;
const ERROR_CODE_HARD_FLAG = 'error.authentication.hard.flag';

export class RegistrationFlow extends RegistrationFlowInterface {
  private readonly config: RegistrationFlowConfig;
  private readonly dependencies: RegistrationFlowDependencies;
  private isUserLoggedIn = false;
  private state: StateTypes = StateTypes.NONE;
  private domNode: HTMLElement | null = null;
  private stateChanged?: (state: RegistrationFlowState) => void;
  private userRegistered?: (event: Credentials) => void;
  private RegistrationContainer?: React.ComponentType<RegistrationContainerProps>;

  constructor(config: RegistrationFlowConfig, dependencies: RegistrationFlowDependencies) {
    super();

    this.config = config;
    this.dependencies = dependencies;
  }

  public async init() {
    const { state: sessionManagerState } = await this.dependencies['session-manager'].getState();
    this.isUserLoggedIn = sessionManagerState === SessionState.LOGGED_IN;

    this.dependencies['session-manager'].onStateChanged(({ state }) => {
      this.isUserLoggedIn = state === SessionState.LOGGED_IN;
    });
  }

  public async start(payload: RegistrationFlowPayload) {
    if (this.state !== StateTypes.NONE) {
      throw { error: ErrorTypes.INVALID_STATE };
    }

    if (this.isUserLoggedIn) {
      throw { error: ErrorTypes.USER_ALREADY_LOGGED_IN };
    }

    window.performance?.mark?.('cp:registration:start');
    const { RegistrationContainer } = await import(
      /* webpackChunkName: "RegistrationContainer" */ './components/RegistrationContainer'
    );
    this.RegistrationContainer = RegistrationContainer;
    this.render(payload);
    this.changeState(StateTypes.FORM_VISIBLE);

    return {};
  }

  public cancel = async () => {
    if (this.state !== StateTypes.FORM_VISIBLE || this.domNode === null) {
      throw { error: ErrorTypes.INVALID_STATE };
    }

    ReactDOM.unmountComponentAtNode(this.domNode);
    document.body.removeChild(this.domNode);
    this.domNode = null;
    this.changeState(StateTypes.NONE);

    return {};
  };

  public async getState() {
    return { state: this.state };
  }

  public onStateChanged(callback: OnStateChangedCallback): () => void {
    this.stateChanged = callback;
    // tslint:disable-next-line:no-empty
    return () => {};
  }

  public onUserRegistered(callback: OnUserRegisteredCallback): () => void {
    this.userRegistered = callback;
    // tslint:disable-next-line:no-empty
    return () => {};
  }

  private render({ regSource, nui }: RegistrationFlowPayload) {
    this.domNode = document.createElement('div');
    document.body.appendChild(this.domNode);
    const RegistrationContainer = this.RegistrationContainer;
    const chatRequestHandler = this.isLivePersonAllowed() ? this.chatRequestHandler : undefined;

    if (RegistrationContainer) {
      ReactDOM.render(
        <RegistrationContainer
          environment={this.config.environment}
          jurisdiction={this.config.jurisdiction}
          locale={this.config.locale}
          regSource={regSource || this.config.regSource}
          nui={nui || this.config.nui}
          dataAccessService={this.dependencies['data-access-service']}
          onClose={this.cancel}
          onSubmit={this.onSubmit}
          onSubmitFailed={this.onSubmitFailed}
          onSubmitSuccess={this.onSubmitSuccess}
          onActionRequested={this.onActionRequested}
          chatRequestHandler={chatRequestHandler}
          login={this.login}
        />,
        this.domNode
      );
    }
  }

  private changeState(state: StateTypes) {
    if (state !== this.state) {
      this.state = state;
      if (this.stateChanged) {
        this.stateChanged({ state });
      }
    }
  }

  private onSubmit = () => {
    window.performance?.mark?.('cp:registration:submit-start');
    this.changeState(StateTypes.AWAIT_ACCOUNT);
  };

  private onSubmitFailed = () => {
    window.performance?.mark?.('cp:registration:submit-finish');
    this.changeState(StateTypes.FORM_VISIBLE);
  };

  private onSubmitSuccess = (data: OnSubmitSuccessArguments) => {
    window.performance?.mark?.('cp:registration:submit-finish');
    if (this.userRegistered) {
      this.userRegistered(data);
    }
    this.changeState(StateTypes.FORM_VISIBLE);
  };

  private onActionRequested: OnActionRequested = (eventId, context) => {
    this.dependencies['account-actions'].execute({
      eventId,
      context: { ...context, userFlow: 'registration' },
    });
    this.cancel();
  };

  private chatRequestHandler = async (data: ChatRequestArguments) => {
    const fallbackUrl = this.config.livePersonFallbackUrl || data.chatUrl;
    const engagementName = this.config.engagementName || data.engagementName || undefined;
    const livePersonClient =
      window.WH.bootstrapper.features['live-chat-flow'] ||
      (window.WH.LivePerson && window.WH.LivePerson.client) ||
      undefined;
    try {
      const livePersonResponse = await livePersonClient.activateEngagement({ name: engagementName });
      if (livePersonResponse instanceof Error) {
        (window.consoleReference || window.console).log(livePersonResponse);
        window.open(fallbackUrl);
      }
    } catch (error) {
      (window.consoleReference || window.console).log(error);
      window.open(fallbackUrl);
    }
  };

  private isLivePersonAllowed() {
    return (
      this.config.jurisdiction === Jurisdiction.COM &&
      this.config.locale === 'en-gb' &&
      this.config.isLivePersonSupported
    );
  }

  private login: LoginHandler = (username: string, password: string, token?: string) => {
    this.changeState(StateTypes.AWAIT_LOGIN);

    return new Promise((resolve, reject) => {
      this.dependencies['session-manager']
        .logIn({ username, password, token })
        .then((state: LoginState) => {
          this.changeState(StateTypes.FORM_VISIBLE);

          if (this.config.jurisdiction === Jurisdiction.IT) {
            window.setTimeout(() => {
              this.onActionRequested(ACCOUNT_EVENT_IDS.DEPOSIT_REQUIRED);
            }, DEPOSIT_REQUIRED_ACTION_TIMEOUT);
          }

          resolve(state);
        })
        .catch((state: LoginState) => {
          this.changeState(StateTypes.FORM_VISIBLE);
          if (state?.error?.code === ERROR_CODE_HARD_FLAG) {
            this.onActionRequested(ACCOUNT_EVENT_IDS.HARD_FLAG_REQUESTED, { data: state?.error?.data });
          }
          reject(state);
        });
    });
  };
}
